import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Edit, Trash2, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK = {
  title: '', slug: '', excerpt: '', content: '', author_name: '',
  category: 'General', tags: '', cover_image_url: '', published: false,
};

export default function BlogAdminPage() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const token = localStorage.getItem('token');
    const r = await axios.get(`${API}/blog/admin`, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(r.data);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...BLANK });
  const openEdit = (p) => setEditing({ ...p, tags: (p.tags || []).join(', ') });

  const save = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    const body = {
      title: editing.title, slug: editing.slug || undefined,
      excerpt: editing.excerpt, content: editing.content,
      author_name: editing.author_name || undefined,
      category: editing.category, tags: (editing.tags || '').split(',').map((t) => t.trim()).filter(Boolean),
      cover_image_url: editing.cover_image_url || undefined,
      published: editing.published,
    };
    try {
      if (editing.id) {
        await axios.put(`${API}/blog/${editing.id}`, body, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/blog/`, body, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(editing.id ? 'Post updated' : 'Post created');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    const token = localStorage.getItem('token');
    await axios.delete(`${API}/blog/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success('Deleted');
    load();
  };

  return (
    <div className="space-y-6" data-testid="blog-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-serif font-bold">Blog</h1>
          <p className="text-sm text-muted-foreground">Author articles for the public blog at /blog.</p>
        </div>
        <Button onClick={openNew} data-testid="blog-new-btn"><Plus className="w-4 h-4 mr-1" /> New Post</Button>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-3 text-left">Title</th>
              <th className="p-3 text-left">Category</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Updated</th>
              <th className="p-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {posts.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No posts yet.</td></tr>
            )}
            {posts.map((p) => (
              <tr key={p.id} className="border-t hover:bg-muted/20" data-testid={`blog-row-${p.slug}`}>
                <td className="p-3 font-medium">{p.title}</td>
                <td className="p-3 text-muted-foreground">{p.category}</td>
                <td className="p-3">
                  {p.published ? (
                    <span className="text-emerald-700 text-xs font-semibold">Published</span>
                  ) : (
                    <span className="text-muted-foreground text-xs">Draft</span>
                  )}
                </td>
                <td className="p-3 text-xs text-muted-foreground">
                  {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : ''}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {p.published && (
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer"
                       className="inline-block mr-2 text-emerald-700"><ExternalLink className="w-4 h-4 inline" /></a>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="mr-1"><Edit className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(p)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && (
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.id ? 'Edit' : 'New'} post</DialogTitle>
              <DialogDescription>
                Write in markdown — headings, lists, links and bold all render on the public /blog/:slug page.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Title *</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="blog-title" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Slug (auto if blank)</Label>
                  <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="my-post" />
                </div>
                <div><Label>Category</Label>
                  <Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Author</Label>
                  <Input value={editing.author_name} onChange={(e) => setEditing({ ...editing, author_name: e.target.value })} placeholder="Bison Team" />
                </div>
                <div><Label>Tags (comma-separated)</Label>
                  <Input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} placeholder="hospitality, procurement" />
                </div>
              </div>
              <div><Label>Cover image URL <span className="text-xs text-muted-foreground">(or upload below)</span></Label>
                <Input value={editing.cover_image_url || ''} onChange={(e) => setEditing({ ...editing, cover_image_url: e.target.value })} placeholder="https://…" />
                <input
                  type="file"
                  accept="image/*"
                  className="mt-2 text-xs"
                  data-testid="blog-cover-upload"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 800_000) { toast.error('Image too large (max 800 KB)'); return; }
                    const reader = new FileReader();
                    reader.onload = () => setEditing((s) => ({ ...s, cover_image_url: reader.result }));
                    reader.readAsDataURL(f);
                  }}
                />
                {editing.cover_image_url && (
                  <img src={editing.cover_image_url} alt="Cover preview" className="mt-2 h-24 rounded object-cover" />
                )}
              </div>
              <div><Label>Excerpt (short summary)</Label>
                <Textarea rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
              </div>
              <div><Label>Content (Markdown)</Label>
                <Textarea rows={12} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                  placeholder="## Heading&#10;&#10;Your article in **markdown**…" data-testid="blog-content-input" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} data-testid="blog-published" />
                <Label>Published (visible on /blog)</Label>
              </div>
              <Button onClick={save} disabled={saving || !editing.title || !editing.content} className="w-full" data-testid="blog-save">
                {saving ? 'Saving…' : editing.id ? 'Save changes' : 'Create post'}
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
